import { ReactNode, useEffect, useState } from 'react';
import cn from "classnames";
import { Title2, SubTitle3 } from "@components/new/Typo";
import { Badge, BadgeType } from "@components/new/Badge";
import { Tag } from "@components/new/Tag";
import styles from "./Tabs.module.scss";

export interface Tab {
  id: string;
  title: ReactNode | string;
  description?: ReactNode | string;
  count?: number;
  content: ReactNode;
  disable?: boolean;
  order?: number;
  icon?: string | React.FunctionComponent<React.SVGAttributes<SVGElement>>;
  type?: "badge" | "default";
}

interface TabsProps {
  tabs: Tab[];
  defaultTab?: string;
  vertical?: boolean;
  stretch?: boolean;
  type?: "badge" | "default";
}

interface TabsListProps {
  onChange: (tabId: string) => void;
  tabs: Tab[];
  value: string;
  vertical?: boolean;
  stretch?: boolean;
  type?: "badge" | "default";
}

interface TabsContainerProps {
  children: React.ReactNode;
}

interface TabsContentProps {
  tab?: Tab;
  className?: string;
}

interface TabsItemProps {
  tab: Tab;
  onChange: (tabId: string) => void;
  value: string;
  type?: string;
}

export const TabsContainer = ({ children }: TabsContainerProps) => (
  <div className={styles.tabs}>
    {children}
  </div>
)

export const TabsItem = ({
  onChange,
  tab,
  value,
  type,
}: TabsItemProps) => {
  const handleClick = (tabId: string, disable: boolean) => {
    if (disable) return;
    onChange(tabId);
  };

  const isActive = tab.id === value;

  if (type === "badge") {
    return (
      <Badge
        type={isActive ? BadgeType.PRIMARY : BadgeType.OUTLINE}
        onClick={() => handleClick(tab.id, tab.disable ?? false)}
        iconLeft={tab.icon}
      >
        {tab.title}
        <Tag active={isActive} value={tab.count || 0} />
      </Badge>
    )
  }

  return (
    <li
      key={tab.id}
      className={cn(
        styles.listItem,
        isActive && styles.active,
        tab.disable && styles.disable,
      )}
      style={{ order: tab.order }}
      onClick={() => handleClick(tab.id, tab.disable ?? false)}
    >
      {tab.icon && (
        <div className={styles.icon}>
          {typeof tab.icon === "string" && (
            <img alt="icon" src={tab.icon} />
          )}
          {typeof tab.icon === "function" && tab.icon({})}
        </div>
      )}
      <Title2>{tab.title}</Title2>
      {tab.count !== undefined && tab.count > 0 && (
        <div className={styles.count}>
          <SubTitle3>{tab.count > 99 ? "99+" : tab.count}</SubTitle3>
        </div>
      )}
    </li>
  )
}

export const TabsList = ({
  onChange,
  tabs,
  value,
  vertical,
  stretch,
  type,
}: TabsListProps) => {
  const activeTab = tabs.find(tab => (tab.id === value));

  return (
    <div className={styles.tabsListGroup}>
      <ul className={cn(
        styles.list,
        styles[`list-${type}`],
        vertical && styles.vertical,
        stretch && styles.stretch,
      )}>
        {(tabs || []).map((tab: Tab) => (
          <TabsItem
            key={tab.id}
            tab={tab}
            value={value}
            type={type}
            onChange={onChange}
          />
        ))}
      </ul>
      {activeTab?.description ? (
        <div className={styles.description}>
          <SubTitle3>{activeTab?.description}</SubTitle3>
        </div>
      ) : null}
    </div>
  )
}

export const TabsContent = ({ tab, className }: TabsContentProps) => {
  if (!tab || !tab.content) return null
  return (
    <div key={tab.id} className={cn(styles.content, className)}>
      {tab.content}
    </div>
  )
}

const Tabs = ({
  tabs,
  defaultTab,
  vertical,
  stretch,
  type,
}: TabsProps) => {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id);

  const activeTabData = tabs.find((tab) => (tab.id === activeTab));

  useEffect(() => {
    if (!activeTab || !tabs.find(t => (t.id === activeTab))) {
      setActiveTab(defaultTab || tabs[0]?.id);
    }
  }, [tabs]);

  return (
    <TabsContainer>
      <TabsList
        tabs={tabs}
        onChange={setActiveTab}
        value={activeTab}
        vertical={vertical}
        stretch={stretch}
        type={type}
      />

      <TabsContent tab={activeTabData} />

    </TabsContainer>
  );
};

export default Tabs;
